import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiMap, FiMapPin, FiAlertCircle, FiBarChart2 } from 'react-icons/fi';
import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { MapaOuvidoria, type PinoMapa } from '../../components/MapaOuvidoria';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';

const TIPO_LABEL: Record<string, string> = {
  sugestao: 'Sugestão',
  reclamacao: 'Reclamação',
  denuncia: 'Denúncia',
  elogio: 'Elogio',
  solicitacao_informacao: 'Solicitação de informação',
};

const STATUS_GRUPO: Record<string, 'aberto' | 'andamento' | 'concluido' | 'outro'> = {
  aberto: 'aberto',
  aguardando_solicitante: 'aberto',
  em_tramitacao: 'andamento',
  em_analise: 'andamento',
  em_diligencia: 'andamento',
  deferido: 'concluido',
  concluido: 'concluido',
  indeferido: 'concluido',
  arquivado: 'outro',
  cancelado: 'outro',
};

export function OuvidoriaMapaPage() {
  const [pinos, setPinos] = useState<PinoMapa[]>([]);
  const [estab, setEstab] = useState<{ nome: string; cor_primaria: string | null } | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [filtroTipo, setFiltroTipo] = useState<string>('');
  const [filtroStatus, setFiltroStatus] = useState<string>('');

  useEffect(() => {
    const id = getEstabelecimentoAtualId();
    Promise.all([
      supabase.rpc('ouvidoria_mapa_publico', { p_estabelecimento_id: id }),
      supabase.from('estabelecimentos').select('nome, cor_primaria').eq('id', id).maybeSingle(),
    ])
      .then(([mapResp, estabResp]) => {
        if (mapResp.error) throw mapResp.error;
        setPinos((mapResp.data ?? []) as PinoMapa[]);
        setEstab(estabResp.data as { nome: string; cor_primaria: string | null } | null);
      })
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro ao carregar.'))
      .finally(() => setCarregando(false));
  }, []);

  const pinosFiltrados = useMemo(() => {
    return pinos.filter((p) => {
      if (filtroTipo && p.tipo_manifestacao !== filtroTipo) return false;
      if (filtroStatus && STATUS_GRUPO[p.status] !== filtroStatus) return false;
      return true;
    });
  }, [pinos, filtroTipo, filtroStatus]);

  const totaisPorTipo = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of pinos) {
      if (p.tipo_manifestacao) map.set(p.tipo_manifestacao, (map.get(p.tipo_manifestacao) ?? 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [pinos]);

  return (
    <PortalPage
      titulo="Mapa da Ouvidoria"
      subtitulo={`${estab?.nome ? estab.nome + ' · ' : ''}Manifestações públicas com geolocalização`}
      icone={<FiMap size={26} />}
      voltarHref="/"
      largura="larga"
      acoes={
        <Link
          to="/mapa"
          className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-2 text-sm font-medium text-white ring-1 ring-white/20 transition hover:bg-white/20"
        >
          <FiMap size={15} /> Todas as demandas
        </Link>
      }
    >
      {carregando ? (
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      ) : erro ? (
        <Alert variante="erro">{erro}</Alert>
      ) : pinos.length === 0 ? (
        <SectionCard>
          <div className="text-center py-12">
            <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
              <FiMapPin className="text-slate-400" size={26} />
            </div>
            <p className="text-sm font-medium text-slate-700">Ainda não há manifestações com localização registrada.</p>
            <p className="text-xs text-slate-500 mt-1">
              Quando cidadãos abrirem manifestações com endereço, elas aparecerão aqui.
            </p>
          </div>
        </SectionCard>
      ) : (
        <div className="space-y-5">
          {/* Filtros + estatísticas */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_240px] gap-5">
            <SectionCard titulo="Filtros" icone={<FiMapPin size={18} />}>
              <div className="flex items-center gap-3 flex-wrap">
                <select
                  value={filtroTipo}
                  onChange={(e) => setFiltroTipo(e.target.value)}
                  className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
                >
                  <option value="">Todos os tipos</option>
                  {Object.entries(TIPO_LABEL).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
                <select
                  value={filtroStatus}
                  onChange={(e) => setFiltroStatus(e.target.value)}
                  className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
                >
                  <option value="">Todos os status</option>
                  <option value="aberto">Abertas / aguardando</option>
                  <option value="andamento">Em andamento</option>
                  <option value="concluido">Concluídas</option>
                </select>
                <div className="ml-auto text-sm text-slate-500">
                  {pinosFiltrados.length} de {pinos.length}
                </div>
              </div>
            </SectionCard>

            <SectionCard titulo="Por tipo" icone={<FiBarChart2 size={18} />}>
              {totaisPorTipo.length === 0 ? (
                <div className="text-xs text-slate-500">—</div>
              ) : (
                <ul className="space-y-1 text-xs">
                  {totaisPorTipo.map(([tipo, qtd]) => (
                    <li key={tipo} className="flex items-baseline justify-between gap-2">
                      <span className="text-slate-700 truncate">{TIPO_LABEL[tipo] ?? tipo}</span>
                      <span className="font-medium text-slate-900 tabular-nums">{qtd}</span>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
          </div>

          {/* Mapa */}
          <SectionCard semPadding corpoClassName="p-2">
            <MapaOuvidoria pinos={pinosFiltrados} altura="600px" />
          </SectionCard>

          {/* Legenda */}
          <div className="flex items-center gap-6 text-xs text-slate-600 flex-wrap">
            <Legenda cor="#f59e0b" label="Aberta / aguardando" />
            <Legenda cor="#3b82f6" label="Em andamento" />
            <Legenda cor="#10b981" label="Concluída / deferida" />
            <Legenda cor="#ef4444" label="Indeferida" />
            <Legenda cor="#94a3b8" label="Arquivada" />
          </div>

          <Alert variante="info">
            <strong>Sobre estes dados.</strong> Manifestações com modo de identificação <em>sigiloso</em> não
            aparecem neste mapa público — apenas <em>identificadas</em> e <em>anônimas</em> com localização.
            <FiAlertCircle className="inline ml-1" size={12} />
          </Alert>
        </div>
      )}
    </PortalPage>
  );
}

function Legenda({ cor, label }: { cor: string; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block w-3 h-3 rounded-full border-2 border-white" style={{ background: cor, boxShadow: '0 0 0 1px rgba(0,0,0,0.1)' }} />
      <span>{label}</span>
    </div>
  );
}
